import React from "react";
import { Link } from "react-router-dom";
import { Clock, Stack, PlayCircle, EyeSlash } from "@phosphor-icons/react";

const DEFAULT_THUMBS = [
  "https://images.pexels.com/photos/7648050/pexels-photo-7648050.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMGNvdXJzZSUyMHRodW1ibmFpbHxlbnwwfHx8fDE3ODAyMzQ5NTh8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMGNvdXJzZSUyMHRodW1ibmFpbHxlbnwwfHx8fDE3ODAyMzQ5NTh8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/flagged/photo-1576485436509-a7d286952b65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMGNvdXJzZSUyMHRodW1ibmFpbHxlbnwwfHx8fDE3ODAyMzQ5NTh8MA&ixlib=rb-4.1.0&q=85",
];

// Stable fallback thumbnail per course
const fallbackThumb = (courseId) => {
  if (!courseId) return DEFAULT_THUMBS[0];
  let h = 0;
  for (let i = 0; i < courseId.length; i++) h = (h * 31 + courseId.charCodeAt(i)) >>> 0;
  return DEFAULT_THUMBS[h % DEFAULT_THUMBS.length];
};

export default function CourseCard({ course, progress, to }) {
  const totalLessons = (course.modules || []).reduce((sum, m) => sum + (m.lessons?.length || 0), 0);
  const moduleCount = (course.modules || []).length;
  const thumb = course.thumbnail_url || fallbackThumb(course.id);
  const percent = progress?.percent ?? null;
  const totalDuration = (course.modules || []).reduce(
    (s, m) => s + (m.lessons || []).reduce((ss, l) => ss + (l.duration_seconds || 0), 0),
    0
  );
  const totalMin = Math.round(totalDuration / 60);

  return (
    <Link
      to={to || `/courses/${course.id}`}
      data-testid={`course-card-${course.id}`}
      className="group flex flex-col bg-white border border-[#EAEAE6] rounded-xl overflow-hidden hover:shadow-xl hover:-translate-y-1 hover:border-[#E25822]/30 transition-all duration-300"
    >
      <div className="relative aspect-[16/9] bg-[#F4F4F0] overflow-hidden">
        <img
          src={thumb}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] uppercase tracking-[0.12em] font-semibold text-[#1C1C19] shadow-sm">
            {course.category || "General"}
          </span>
          {!course.published && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#993333] text-white text-[10px] uppercase tracking-[0.12em] font-semibold">
              <EyeSlash size={10} /> Draft
            </span>
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
          <PlayCircle size={48} weight="fill" className="text-white drop-shadow-lg" />
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#8A8A85] mb-2">
          {course.level || "Beginner"}
        </div>
        <h3 className="font-heading font-semibold text-lg text-[#1C1C19] leading-snug tracking-tight line-clamp-2 mb-2 group-hover:text-[#E25822] transition-colors">
          {course.title}
        </h3>
        <p className="text-sm text-[#5C5C58] line-clamp-2 mb-4 leading-relaxed flex-1">
          {course.description || "No description provided."}
        </p>

        <div className="flex items-center gap-4 text-xs text-[#5C5C58] pt-3 border-t border-[#F4F4F0]">
          <span className="flex items-center gap-1.5">
            <Stack size={13} /> {moduleCount} {moduleCount === 1 ? "module" : "modules"}
          </span>
          <span className="flex items-center gap-1.5">
            <PlayCircle size={13} /> {totalLessons} {totalLessons === 1 ? "lesson" : "lessons"}
          </span>
          {totalMin > 0 && (
            <span className="flex items-center gap-1.5 ml-auto">
              <Clock size={13} /> {totalMin} min
            </span>
          )}
        </div>

        {percent !== null && (
          <div className="mt-4">
            <div className="flex justify-between text-[10px] uppercase tracking-[0.12em] mb-1.5 font-semibold">
              <span className="text-[#5C5C58]">Progress</span>
              <span className="text-[#E25822]" data-testid={`course-progress-${course.id}`}>
                {percent}%
              </span>
            </div>
            <div className="h-1.5 progress-track rounded-full overflow-hidden">
              <div
                className="h-full bg-[#E25822] transition-all duration-500 rounded-full"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}
